// src/pages/Contact.js
import { Link } from 'react-router-dom';

import React, { useState, useEffect } from 'react';
import { FiLinkedin, FiUser, FiArrowLeft, FiSend, FiLoader, FiMail, FiCheckCircle, FiMessageCircle, FiList } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { authApi, ticketApi } from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import { pageDescriptions } from '../data/pageDescriptions';

const Contact = () => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // For tickets history
  const [showHistory, setShowHistory] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  
  useEffect(() => {
    // Optionally load user's past tickets if they toggle to view history
    if (showHistory) {
      loadUserTickets();
    }
  }, [showHistory]);
  
  const loadUserTickets = async () => {
    try {
      setLoadingTickets(true);
      const response = await ticketApi.getUserTickets();
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load your support history');
    } finally {
      setLoadingTickets(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }
    
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }
    
    try {
      setError('');
      setSubmitting(true);
      
      // Use the tickets API to submit the form
      await ticketApi.createTicket(formData);
      
      // Show success
      setSubmitted(true);
      toast.success('Your message has been sent! We will respond to you soon.');
      
      // Clear form
      setFormData({
        subject: '',
        message: '',
        category: 'general'
      });
      
      // Reload tickets if history is open
      if (showHistory) {
        loadUserTickets();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setSubmitted(false);
    setError('');
  };
  
  const formatDate = (dateString) => {
    if (dateString == null) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    if (status == null || typeof status !== 'string') {
      return (
        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    switch (status) {
      case 'open':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
            Open
          </span>
        );
      case 'in_progress':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            In Progress
          </span>
        );
      case 'resolved':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Resolved
          </span>
        );
      case 'closed':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Closed
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title={pageDescriptions['/contact'].title}
        description={pageDescriptions['/contact'].description}
        whatYouCanDo={pageDescriptions['/contact'].whatYouCanDo}
        whatYouShouldDo={pageDescriptions['/contact'].whatYouShouldDo}
        actions={
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            aria-label={showHistory ? 'Show contact form' : 'View my support tickets'}
          >
            {showHistory ? (
              <>
                <FiMessageCircle className="mr-2 -ml-1 h-4 w-4" />
                Contact Form
              </>
            ) : (
              <>
                <FiList className="mr-2 -ml-1 h-4 w-4" />
                View My Tickets
              </>
            )}
          </button>
        }
      />
      
      {showHistory ? (
        // Show tickets history
        <div className="card overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Your Support Tickets</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              View the status of your previous support requests
            </p>
          </div>
          
          {loadingTickets ? (
            <div className="flex justify-center py-8">
              <FiLoader className="animate-spin h-8 w-8 text-primary-500" />
            </div>
          ) : tickets.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <li key={ticket.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-primary-600 truncate">
                        {ticket.subject}
                      </div>
                      <div className="ml-2 flex-shrink-0 flex">
                        {getStatusBadge(ticket.status)}
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <span className="truncate">{ticket.category}</span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <span>{formatDate(ticket.createdAt)}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-900">{ticket.message}</p>
                    </div>
                    
                    {ticket.adminResponse && (
                      <div className="mt-4 bg-gray-50 rounded-md p-3">
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Response
                        </h4>
                        <p className="mt-1 text-sm text-gray-700">{ticket.adminResponse}</p>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <FiMail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets</h3>
              <p className="mt-1 text-sm text-gray-500">
                You haven't submitted any support tickets yet.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  aria-label="Contact support"
                  onClick={() => setShowHistory(false)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <FiMessageCircle className="-ml-1 mr-2 h-5 w-5" />
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Show contact form
        <div className="card overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Get in Touch
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Have a question or need help? Send us a message and we'll get back to you as soon as possible.
            </p>
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            {submitted ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <FiCheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900">Message Sent!</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Thank you for your message. Our team will review it and get back to you shortly.
                </p>
                <div className="mt-6">
                  <button
                    type="button"
                    aria-label="Send another message"
                    onClick={resetForm}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Send Another Message
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
                
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="feature_request">Feature Request</option>
                    <option value="bug_report">Bug Report</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    aria-label="Submit contact form"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend className="-ml-1 mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;